import { cn } from '@/utils';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { useReactFlow, useViewport } from '@xyflow/react';
import type { FC } from 'react';
import { Fragment, memo, useCallback, useState } from 'react';
import {
  PortalToFollowElem,
  PortalToFollowElemContent,
  PortalToFollowElemTrigger,
} from '../components/portal-to-follow-elem';
import { useWorkflowReadOnly } from '../hooks/use-workflow';
import TipPopup from './tip-popup';

const ZoomTypeEnum = {
  zoomIn: 'zoomIn',
  zoomOut: 'zoomOut',
  zoomToFit: 'zoomToFit',
  zoomTo25: 'zoomTo25',
  zoomTo50: 'zoomTo50',
  zoomTo75: 'zoomTo75',
  zoomTo100: 'zoomTo100',
  zoomTo200: 'zoomTo200',
} as const;

type ZoomType = (typeof ZoomTypeEnum)[keyof typeof ZoomTypeEnum];

const ZoomInOut: FC = () => {
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const { zoom } = useViewport();
  // const { handleSyncWorkflowDraft } = useNodesSyncDraft();
  const [open, setOpen] = useState(false);
  const { getWorkflowReadOnly } = useWorkflowReadOnly();

  const ZOOM_IN_OUT_OPTIONS = [
    [
      {
        key: ZoomTypeEnum.zoomTo200,
        text: '200%',
      },
      {
        key: ZoomTypeEnum.zoomTo100,
        text: '100%',
      },
      {
        key: ZoomTypeEnum.zoomTo75,
        text: '75%',
      },
      {
        key: ZoomTypeEnum.zoomTo50,
        text: '50%',
      },
      {
        key: ZoomTypeEnum.zoomTo25,
        text: '25%',
      },
    ],
    [
      {
        key: ZoomTypeEnum.zoomToFit,
        text: 'Fit',
      },
    ],
  ];

  const handleZoom = (type: ZoomType) => {
    if (getWorkflowReadOnly()) return;

    switch (type) {
      case ZoomTypeEnum.zoomToFit:
        fitView();
        break;
      case ZoomTypeEnum.zoomTo25:
        zoomTo(0.25);
        break;
      case ZoomTypeEnum.zoomTo50:
        zoomTo(0.5);
        break;
      case ZoomTypeEnum.zoomTo75:
        zoomTo(0.75);
        break;
      case ZoomTypeEnum.zoomTo100:
        zoomTo(1);
        break;
      case ZoomTypeEnum.zoomTo200:
        zoomTo(2);
        break;
    }

    // handleSyncWorkflowDraft();
  };

  const handleTrigger = useCallback(() => {
    if (getWorkflowReadOnly()) return;

    setOpen((v) => !v);
  }, []);

  return (
    <PortalToFollowElem
      placement="top-start"
      open={open}
      onOpenChange={setOpen}
      offset={{
        mainAxis: 4,
        crossAxis: -2,
      }}
    >
      <PortalToFollowElemTrigger asChild onClick={handleTrigger}>
        <div
          className={`
          p-0.5 h-9 cursor-pointer text-[13px] text-gray-500 font-medium rounded-lg bg-white shadow-lg border-[0.5px] border-gray-100
          `}
        >
          {/* ${workflowReadOnly && '!cursor-not-allowed opacity-50'} */}
          <div
            className={cn(
              'flex items-center justify-between w-[98px] h-8 hover:bg-gray-50 rounded-lg',
              open && 'bg-gray-50',
            )}
          >
            <TipPopup title="缩小">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer hover:bg-black/5"
                onClick={(e) => {
                  e.stopPropagation();
                  zoomOut();
                }}
              >
                <ZoomOutOutlined className="w-4 h-4" />
              </div>
            </TipPopup>
            <div className="w-[34px]">
              {parseFloat(`${zoom * 100}`).toFixed(0)}%
            </div>
            <TipPopup title="放大">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer hover:bg-black/5"
                onClick={(e) => {
                  e.stopPropagation();
                  zoomIn();
                }}
              >
                <ZoomInOutlined className="w-4 h-4" />
              </div>
            </TipPopup>
          </div>
        </div>
      </PortalToFollowElemTrigger>
      <PortalToFollowElemContent className="z-10">
        <div className="w-[145px] rounded-lg border-[0.5px] border-gray-200 bg-white shadow-lg">
          {ZOOM_IN_OUT_OPTIONS.map((options, i) => (
            <Fragment key={i}>
              {i !== 0 && <div className="h-[1px] bg-gray-100" />}
              <div className="p-1">
                {options.map((option) => (
                  <div
                    key={option.key}
                    className="flex items-center justify-between px-3 h-8 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                    onClick={() => handleZoom(option.key)}
                  >
                    {option.text}
                  </div>
                ))}
              </div>
            </Fragment>
          ))}
        </div>
      </PortalToFollowElemContent>
    </PortalToFollowElem>
  );
};

export default memo(ZoomInOut);
